import React from 'react';
import { Box, Typography, CircularProgress, Chip, Divider, IconButton, Tooltip, Card, CardContent, CardActions, Button } from '@mui/material';
import { styled } from '@mui/system';
import { FileText, Check, Star, Settings, User, Crown, Eye } from 'lucide-react';

const StyledCard = styled(Card)(({ theme, isDefault }) => ({
  position: 'relative',
  maxWidth: 380,
  margin: theme.spacing(2),
  borderRadius: '16px',
  overflow: 'visible',
  background: theme.palette.mode === 'dark'
    ? isDefault 
      ? 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)'
      : 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
    : isDefault
      ? 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #e2e8f0 100%)'
      : 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
  border: isDefault 
    ? `2px solid ${theme.palette.mode === 'dark' ? '#10b981' : '#059669'}`
    : `1px solid ${theme.palette.mode === 'dark' ? '#334155' : '#e5e7eb'}`,
  boxShadow: isDefault
    ? theme.palette.mode === 'dark' 
      ? '0 10px 25px rgba(16, 185, 129, 0.2), 0 4px 12px rgba(0, 0, 0, 0.3)'
      : '0 10px 25px rgba(5, 150, 105, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)'
    : theme.palette.mode === 'dark'
      ? '0 4px 12px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)'
      : '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: isDefault
      ? theme.palette.mode === 'dark'
        ? '0 20px 40px rgba(16, 185, 129, 0.3), 0 8px 16px rgba(0, 0, 0, 0.4)'
        : '0 20px 40px rgba(5, 150, 105, 0.2), 0 8px 16px rgba(0, 0, 0, 0.15)'
      : theme.palette.mode === 'dark'
        ? '0 12px 24px rgba(0, 0, 0, 0.4), 0 6px 12px rgba(0, 0, 0, 0.3)'
        : '0 12px 24px rgba(0, 0, 0, 0.12), 0 6px 12px rgba(0, 0, 0, 0.08)',
  },
}));

const DefaultBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -8,
  right: -8,
  zIndex: 10,
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  borderRadius: '50%',
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.7)',
    },
    '70%': {
      boxShadow: '0 0 0 10px rgba(16, 185, 129, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(16, 185, 129, 0)',
    },
  },
}));

const TypeChip = styled(Chip)(({ theme, templateType }) => ({
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '0.75rem',
  height: 28,
  background: templateType === 'Order-Based' 
    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  color: '#ffffff',
  border: 'none',
  '& .MuiChip-icon': {
    color: '#ffffff',
    marginLeft: '8px',
  },
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '10px 20px',
  minHeight: 44,
  fontSize: '0.875rem',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  ...(variant === 'contained' && {
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)',
      transform: 'translateY(-2px)',
    },
    '&:disabled': {
      background: theme.palette.mode === 'dark' ? '#374151' : '#d1d5db',
      color: theme.palette.mode === 'dark' ? '#6b7280' : '#9ca3af',
      boxShadow: 'none',
    },
  }),
  ...(variant === 'outlined' && {
    borderColor: theme.palette.mode === 'dark' ? '#475569' : '#d1d5db',
    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#374151',
    background: 'transparent',
    '&:hover': {
      borderColor: theme.palette.mode === 'dark' ? '#64748b' : '#9ca3af',
      background: theme.palette.mode === 'dark' ? 'rgba(71, 85, 105, 0.1)' : 'rgba(156, 163, 175, 0.1)',
      transform: 'translateY(-2px)',
    },
  }),
}));

const StatsCard = styled('div')(({ theme }) => ({
  padding: '24px',
  borderRadius: '16px',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#334155' : '#e5e7eb'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 12px rgba(0, 0, 0, 0.3)'
    : '0 4px 12px rgba(0, 0, 0, 0.08)',
}));

const TemplateGrid = ({
  templates,
  loading,
  defaultTemplates,
  getTemplateName,
  getDefaultKey,
  handleSelectTemplate,
  handleOpenPreviewModal,
}) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(auto-fit, minmax(350px, 1fr))',
        },
        gap: 4,
        justifyItems: 'center',
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b' }}>
              Loading templates...
            </Typography>
          </Box>
        </Box>
      ) : templates.length === 0 ? (
        <StatsCard sx={{ textAlign: 'center', py: 6 }}>
          <FileText size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
          <Typography
            variant="h6"
            sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b', mb: 2 }}
          >
            No templates available
          </Typography>
          <Typography sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#64748b' : '#9ca3af' }}>
            Create your first template to get started
          </Typography>
        </StatsCard>
      ) : (
        templates.map((template) => {
          const defaultKey = getDefaultKey(template.type);
          const isDefault = defaultKey && defaultTemplates[defaultKey] === template._id;

          return (
            <StyledCard key={template._id} isDefault={isDefault}>
              {isDefault && (
                <DefaultBadge>
                  <Crown size={20} color="#ffffff" />
                </DefaultBadge>
              )}

              <CardContent sx={{ p: 4, position: 'relative' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{
                      color: (theme) => theme.palette.mode === 'dark' ? '#f1f5f9' : '#0f172a',
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      lineHeight: 1.3,
                      flex: 1,
                      mr: 2,
                    }}
                  >
                    {template.name}
                  </Typography>
                  <TypeChip
                    label={template.type}
                    templateType={template.type}
                    size="small"
                    icon={template.type === 'Order-Based' ? <Settings size={14} /> : <User size={14} />}
                  />
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b',
                    mb: 3,
                    lineHeight: 1.6,
                    minHeight: '48px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {template.description || 'No description provided for this template'}
                </Typography>

                {isDefault && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                      p: 2,
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                      border: (theme) =>
                        `1px solid ${theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.2)'}`,
                    }}
                  >
                    <Star size={16} color="#10b981" fill="#10b981" />
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#10b981',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Default {template.type} Template
                    </Typography>
                  </Box>
                )}
              </CardContent>

              <Divider
                sx={{
                  mx: 2,
                  borderColor: (theme) => theme.palette.mode === 'dark' ? '#334155' : '#e5e7eb',
                  opacity: 0.6,
                }}
              />

              <CardActions sx={{ p: 4, pt: 3, gap: 2 }}>
                <ActionButton
                  fullWidth
                  variant="contained"
                  onClick={() => handleSelectTemplate(template._id, template.type)}
                  disabled={loading || isDefault}
                  startIcon={isDefault ? <Check size={18} /> : <Star size={18} />}
                >
                  {isDefault ? 'Currently Selected' : 'Set as Default'}
                </ActionButton>

                <Tooltip title="Preview Template">
                  <IconButton
                    onClick={() => handleOpenPreviewModal(template.htmlContent)}
                    disabled={loading}
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#475569' : '#d1d5db'}`,
                      color: (theme) => theme.palette.mode === 'dark' ? '#e2e8f0' : '#374151',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderColor: (theme) => theme.palette.mode === 'dark' ? '#64748b' : '#9ca3af',
                        background: (theme) =>
                          theme.palette.mode === 'dark' ? 'rgba(71, 85, 105, 0.1)' : 'rgba(156, 163, 175, 0.1)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Eye size={20} />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </StyledCard>
          );
        })
      )}
    </Box>
  );
};

export default TemplateGrid;
